/* Copyright 2024 Marimo. All rights reserved. */

import { mount, unmount } from './mount';
import { store } from './core/state/jotai';
import { runtimeConfigAtom } from './core/runtime/config';

export interface MarimoLibraryOptions {
  serverUrl: string;
  hideChrome?: boolean;
  hideFilename?: boolean;
  filename?: string;
  serverToken?: string;
  authToken?: string;
  aiUrl?: string;
  aiModel?: string; // AI model to use (e.g., 'openai/gpt-4o')
  gamgeeAuthToken?: string; // Gamgee JWT token for AI endpoint authentication
  configOverrides?: any;
}

/**
 * Mount a Marimo editor into a DOM element.
 * 
 * This function provides a simplified interface for embedding the Marimo editor
 * in any web application. It handles server URL configuration and chrome hiding
 * automatically.
 * 
 * @param element - The DOM element to mount the editor into
 * @param options - Configuration options for the editor
 * @returns Error if mounting failed, undefined if successful
 * 
 * @example
 * ```typescript
 * const error = mountMarimoEditor(document.getElementById('editor'), {
 *   serverUrl: 'http://localhost:2718',
 *   hideChrome: true,
 *   filename: 'notebook.py'
 * });
 * 
 * if (error) {
 *   console.error('Failed to mount editor:', error);
 * }
 * ```
 */
export function mountMarimoEditor(
  element: HTMLElement, 
  options: MarimoLibraryOptions
): Error | undefined {
  // Add the scoping class to the mount element
  element.classList.add('marimo-editor-root');
  
  // Set runtime config before mount to override server URL
  store.set(runtimeConfigAtom, {
    url: options.serverUrl,
    serverToken: options.serverToken || '',
    authToken: options.authToken || null,
    aiUrl: options.aiUrl || null,
    gamgeeAuthToken: options.gamgeeAuthToken || null
  });

  // Configure mount options
  const mountOptions = {
    mode: 'edit' as const,
    filename: options.filename || 'notebook.py',
    code: '', // Empty code - will be loaded from server
    version: 'library',
    serverToken: options.serverToken || '',
    config: {
      // AI configuration - enables AI features in the UI
      ai: options.aiModel ? {
        models: {
          edit_model: options.aiModel,
          chat_model: options.aiModel,
        }
      } : {}
    }, // User config
    configOverrides: options.configOverrides || {}, // Pass config overrides from options
    appConfig: {}, // App config - using defaults
    view: { showAppCode: true },
    hideFilename: options.hideFilename || false, // Pass hideFilename option
    
    // Runtime configuration with custom server URL
    runtimeConfig: [{
      url: options.serverUrl,
      authToken: options.authToken || null,
      aiUrl: options.aiUrl || null,
      gamgeeAuthToken: options.gamgeeAuthToken || null
    }],

    // Optional parameters
    session: null,
    notebook: null,
    fileStores: []
  };

  // Mount with configuration
  return mount(mountOptions, element);
}

/**
 * Unmount a Marimo editor.
 * 
 * This function properly cleans up the mounted Marimo editor instance,
 * including unmounting React components and resetting internal state.
 * 
 * @example
 * ```typescript
 * // Cleanup on component unmount
 * useEffect(() => {
 *   const error = mountMarimoEditor(element, options);
 *   
 *   return () => {
 *     unmountMarimoEditor();
 *   };
 * }, []);
 * ```
 */
export function unmountMarimoEditor(): void {
  unmount();
}

// Export types for consumers
export type { RuntimeConfig } from './core/runtime/types';